//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useIsFetching } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styled, { useTheme } from 'styled-components';

import { PrimaryLinkButton } from '@components/atoms/button/Button';
import {
  CenteredSpinner,
  InlineSpinner,
} from '@components/atoms/spinner/Spinner';
import { BodyText, SubsectionText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { Product } from '@models/IItemMetadata';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { ProductTagDraggableItem } from '@uia/products/product-tags/ProductTagDraggableItem';
import { move } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';

export interface ProductTagsListProps {
  isCatalogConnected: boolean;
  isProductEditingFeatureDisabled: boolean;
  onEditProductTag: (productTag: Product) => void;
  onRemoveAllProductTags: () => void;
  onRemoveProductTag: (productTag: Product) => void;
  onReorderProductTags: (reorderedProducts: Product[]) => void;
  productTags: Product[];
}

export const ProductTagsList = ({
  onEditProductTag,
  onRemoveAllProductTags,
  onRemoveProductTag,
  onReorderProductTags,
  productTags,
}: ProductTagsListProps) => {
  const {
    cuid,
    isAddingOrRemovingProduct,
    isProcessingProductTagsMutation,
    metadata,
  } = useProductTaggingContext();
  const isReloadingProducts = !!useIsFetching({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsRights,
      QueryKeyConstants.UniversalImageActionsMetadata,
      cuid,
    ],
  });

  const theme = useTheme();

  const [orderedProductTags, setOrderedProductTags] =
    useState<Product[]>(productTags);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset ordered products if the productTags update
  useEffect(() => {
    setOrderedProductTags(productTags);
  }, [productTags]);

  const isLoading = isProcessingProductTagsMutation || isReloadingProducts;

  // Disable dragging if there are 1 or fewer product tags
  const isDragDisabled =
    metadata.disableProductTagging ||
    isLoading ||
    orderedProductTags.length < 1;

  const moveProductTag = (dragIndex: number, hoverIndex: number) => {
    const reorderedProductTags = move(
      orderedProductTags,
      dragIndex,
      hoverIndex,
    );
    setOrderedProductTags(reorderedProductTags);
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <ProductTags>
          {productTags.length > 0 ? (
            <ProductHeader>
              <TitleWrapper>
                <SubsectionText>Tagged Products</SubsectionText>
                {isProcessingProductTagsMutation && <InlineSpinner />}
              </TitleWrapper>
              <PrimaryLinkButton
                isDisabled={isLoading || metadata.disableProductTagging}
                onClick={() => setIsModalOpen(true)}
                trackingId={UiaProductTrackingIds.BULK_REMOVE_PRODUCTS_BUTTON}
                type='button'
              >
                Remove All
              </PrimaryLinkButton>
            </ProductHeader>
          ) : (
            <>
              <TitleWrapper>
                <SubsectionText>Tagged Products</SubsectionText>
                {isProcessingProductTagsMutation && <InlineSpinner />}
              </TitleWrapper>
              <NoProducts>
                <BodyText>No products added</BodyText>
              </NoProducts>
            </>
          )}
          <ProductTagList>
            {orderedProductTags.map((productTag, index) => (
              <ProductTagDraggableItem
                key={`${productTag.id}-${index}`}
                index={index}
                isDisabled={isDragDisabled || !isDefined(productTag.spatialTag)}
                moveItem={moveProductTag}
                onDropItem={() => onReorderProductTags(orderedProductTags)}
                onEditProductTag={onEditProductTag}
                onRemoveProductTag={onRemoveProductTag}
                productTag={productTag}
              />
            ))}
          </ProductTagList>
          {isAddingOrRemovingProduct && <CenteredSpinner />}
        </ProductTags>
      </DndProvider>
      {isModalOpen && (
        <Modal
          closeModal={() => setIsModalOpen(false)}
          contents={`Are you sure you want to remove all ${productTags.length} product tags for this media?`}
          isModalOpen={isModalOpen}
          primaryButtonInfo={{
            buttonContent: `Remove All (${productTags.length})`,
            isDisabled: isLoading,
            onClick: () => {
              onRemoveAllProductTags();
              setIsModalOpen(false);
            },
            trackingId:
              UiaProductTrackingIds.CONFIRM_BULK_REMOVE_PRODUCTS_BUTTON,
          }}
          title='Remove all product tags?'
          zIndexOverlayOverride={theme.zindex.modal} // to ensure the modal is displayed on top of the UIA overlay
        />
      )}
    </>
  );
};

const ProductTags = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    gap: ${theme.spacings[2]};
  `,
);

const TitleWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.spacings[2]};
`,
);

const NoProducts = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacings[4]};
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-radius: ${theme.borderRadii[2]};
    background-color: ${theme.colors.gray0};
  `,
);

const ProductTagList = styled.ul(
  ({ theme }) => `
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
  `,
);

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;
